import { Trans, useLingui } from '@lingui/react/macro'
import { propertyHooks, userHooks } from '$src/lib/trpcHooks'
import { Box, Checkbox, List, Loader, Stack, Text, Title } from '@mantine/core'
import { AppModal, StreetViewCard } from '@markku/components'
import {
  IconCalendar,
  IconCheck,
  IconFileText,
  IconHome,
} from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

interface WelcomePropertyModalProps {
  opened: boolean
  onClose: () => void
  propertyId: number
  propertyAddress: string
}

export function WelcomePropertyModal({
  opened,
  onClose,
  propertyId,
  propertyAddress,
}: WelcomePropertyModalProps) {
  const { t } = useLingui()
  const navigate = useNavigate()
  const { data: property, isLoading } = propertyHooks.useGetById(propertyId, {
    enabled: opened,
  })
  const { mutate: updateMarketingConsent } =
    userHooks.useUpdateMarketingConsent()

  // Local state for marketing consent - defaults to true (opt-out pattern)
  const [marketingConsent, setMarketingConsent] = useState(true)

  const hasStreetViewData =
    property?.address?.streetViewCameraLat &&
    property?.address?.streetViewCameraLng &&
    property?.address?.streetViewHeading

  const handleModalClose = () => {
    // Save marketing consent preference when modal closes
    updateMarketingConsent({ marketingConsent })
    onClose()
  }

  const handleViewPropertyDetails = () => {
    // Wait for the modal to close before navigating to maintenance
    setTimeout(() => {
      navigate({
        to: '/properties/$propertyId',
        params: { propertyId: String(propertyId) },
      })
    }, 350)
    handleModalClose()
  }

  const handleGoToMaintenance = () => {
    // Wait for the modal to close before navigating to maintenance
    setTimeout(() => {
      navigate({ to: '/maintenance' })
    }, 350)
    handleModalClose()
  }

  return (
    <AppModal
      opened={opened}
      onClose={handleModalClose}
      closeOnClickOutside={false}
      closeOnEscape={false}
      size="lg"
      confirmProps={{
        label: t`Property details`,
        onClick: handleViewPropertyDetails,
      }}
      cancelProps={{
        label: t`To maintenance log`,
        onClick: handleGoToMaintenance,
      }}
    >
      {isLoading ? (
        <Stack align="center" py="xl">
          <Loader size="lg" />
        </Stack>
      ) : (
        <Stack gap="xl">
          {/* Success Header */}
          <Stack gap="md" align="center" style={{ textAlign: 'center' }}>
            <Box
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--mantine-color-green-1)',
              }}
            >
              <IconCheck size={36} color="var(--mantine-color-green-7)" />
            </Box>
            <Title order={3}>
              <Trans>Your property has been added!</Trans>
            </Title>
            <Text c="dimmed">{propertyAddress}</Text>
          </Stack>

          {/* Marketing Consent - Above the fold for better visibility */}
          <Checkbox
            checked={marketingConsent}
            size="md"
            onChange={(event) =>
              setMarketingConsent(event.currentTarget.checked)
            }
            label={t`I want to receive Markku maintenance offers and benefits`}
            description={t`Get tips for home maintenance and special offers from our partners`}
          />

          {/* Street View - The WOW Moment (if available) */}
          {hasStreetViewData && property?.address && (
            <StreetViewCard
              address={property.address}
              apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''}
            />
          )}

          {/* Fallback image/placeholder when no street view */}
          {!hasStreetViewData && (
            <Box
              style={{
                width: '100%',
                aspectRatio: '5/2',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #FFF9F0 0%, #FFE8CC 100%)',
              }}
            >
              <IconHome size={80} color="var(--mantine-color-brandOrange-4)" />
            </Box>
          )}

          {/* Feature Highlights */}
          <Stack gap="md">
            <Text size="md" fw={600}>
              <Trans>Your maintenance log is now ready to use</Trans>
            </Text>

            <List spacing="md" size="sm">
              <List.Item
                icon={
                  <IconCalendar
                    size={20}
                    color="var(--mantine-color-brandOrange-6)"
                  />
                }
              >
                <Text fw={500}>
                  <Trans>Annual calendar</Trans>
                </Text>
                <Text size="sm" c="dimmed">
                  <Trans>
                    Follow seasonal maintenance tasks and keep your home in
                    shape all year round
                  </Trans>
                </Text>
              </List.Item>

              <List.Item
                icon={
                  <IconHome
                    size={20}
                    color="var(--mantine-color-brandOrange-6)"
                  />
                }
              >
                <Text fw={500}>
                  <Trans>Property details</Trans>
                </Text>
                <Text size="sm" c="dimmed">
                  <Trans>
                    Save home details, photos and technical system documentation
                  </Trans>
                </Text>
              </List.Item>

              <List.Item
                icon={
                  <IconFileText
                    size={20}
                    color="var(--mantine-color-brandOrange-6)"
                  />
                }
              >
                <Text fw={500}>
                  <Trans>Documents and warranties</Trans>
                </Text>
                <Text size="sm" c="dimmed">
                  <Trans>
                    Store warranties, receipts and important documents safely in
                    one place
                  </Trans>
                </Text>
              </List.Item>
            </List>
          </Stack>
        </Stack>
      )}
    </AppModal>
  )
}
